import { useState, useEffect, useRef } from 'react';

interface Options {
  url: string;
  token: string | null;
  dataKey: string;
  limit?: number;
  params?: Record<string, string>;
  enabled?: boolean;
}

interface Result<T> {
  items: T[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string;
  loadMore: () => void;
  refresh: () => void;
}

export function usePaginatedFetch<T>({
  url,
  token,
  dataKey,
  limit = 20,
  params = {},
  enabled = true,
}: Options): Result<T> {
  const [items, setItems] = useState<T[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  // Stable ref so loadMore always reads the latest cursor without being a dep
  const cursorRef = useRef<string | null>(null);
  cursorRef.current = cursor;

  // Serialize all fetch inputs — changing any of these resets to page 1
  const fetchKey = `${url}|${limit}|${token}|${JSON.stringify(params)}`;

  async function fetchPage(fetchCursor: string | null, append: boolean) {
    if (!token || !enabled) return;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError('');
    }

    try {
      const qs = new URLSearchParams({ limit: String(limit), ...params });
      if (fetchCursor) qs.set('cursor', fetchCursor);

      const res = await fetch(`${url}?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to fetch');

      const newItems: T[] = data[dataKey] || [];
      const next: string | null = data.nextCursor ?? null;

      setItems((prev) => (append ? [...prev, ...newItems] : newItems));
      setCursor(next);
      setHasMore(next !== null);
    } catch (err: any) {
      if (!append) {
        setError(err.message || 'Failed to fetch');
        setHasMore(false);
      }
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }

  // Reset and fetch page 1 whenever any fetch input changes
  useEffect(() => {
    if (!enabled) return;
    setItems([]);
    setCursor(null);
    setHasMore(false);
    fetchPage(null, false);
  }, [fetchKey, enabled]);

  function loadMore() {
    if (!hasMore || loadingMore || loading) return;
    fetchPage(cursorRef.current, true);
  }

  function refresh() {
    setItems([]);
    setCursor(null);
    setHasMore(false);
    fetchPage(null, false);
  }

  return { items, loading, loadingMore, hasMore, error, loadMore, refresh };
}
